import { type Bounds4, type BoxBounds, Vec4, clamp } from 'ag-charts-core';

type Ratios = { min: number; max: number };
type XYRatios = { x: Ratios; y: Ratios };

function normalize(screenMin: number, min: number, screenMax: number, max: number, target: number): number {
    return min + (max - min) * ((target - screenMin) / (screenMax - screenMin));
}

function unnormalize(screenMin: number, min: number, screenMax: number, max: number, ratio: number): number {
    return screenMin + (ratio - min) * ((screenMax - screenMin) / (max - min));
}

function calcWorldAxis(viewportMin: number, viewportMax: number, ratio: Ratios): [number, number] {
    return [
        unnormalize(viewportMin, ratio.min, viewportMax, ratio.max, 0),
        unnormalize(viewportMin, ratio.min, viewportMax, ratio.max, 1),
    ];
}

function calcWorldVec4(viewport: Bounds4, ratioX: Ratios, ratioY: Ratios): Bounds4 {
    const [x1, x2] = calcWorldAxis(viewport.x1, viewport.x2, ratioX);
    const [y1, y2] = calcWorldAxis(viewport.y1, viewport.y2, ratioY);
    return { x1, x2, y1, y2 };
}

/* Pan viewport min (unnormalised, i.e. pixel coords.) by the smallest amount
   such that the viewport range includes the input target range but clamped at
   the world range. The function assumes:
   1)  worldMin <= viewportMin <= viewportMax <= worldMax
   2)  (viewportMax - viewportMin) >= (targetMax - targetMin)
*/
function panAxesUnnormalized(
    worldMin: number,
    worldMax: number,
    viewportMin: number,
    viewportMax: number,
    targetMin: number,
    targetMax: number
): number {
    if (viewportMin <= targetMin && targetMax <= viewportMax) return viewportMin;
    const minDiff = targetMin - viewportMin;
    const maxDiff = targetMax - viewportMax;
    const diff = Math.abs(minDiff) < Math.abs(maxDiff) ? minDiff : maxDiff;
    return clamp(worldMin, viewportMin + diff, worldMax);
}

// The calculations of the new desired viewport (i.e. ZoomState) is done in pixel coords (unnormalised).
// The desired (x, y) for the new viewport is found, the pixel coords are converted into normalized values
export function calcPanToBBoxRatios(
    viewportBBox: BoxBounds,
    ratios: Partial<XYRatios>,
    targetBBox: BoxBounds
): XYRatios {
    const { x: ratioX = { min: 0, max: 1 }, y: ratioY = { min: 0, max: 1 } } = ratios;

    const target = Vec4.from(targetBBox);
    const viewport = Vec4.from(viewportBBox);
    const world = calcWorldVec4(viewport, ratioX, ratioY);

    const x = panAxesUnnormalized(world.x1, world.x2, viewport.x1, viewport.x2, target.x1, target.x2);
    const y = panAxesUnnormalized(world.y1, world.y2, viewport.y1, viewport.y2, target.y1, target.y2);

    const result: XYRatios = {
        x: {
            min: normalize(viewport.x1, ratioX.min, viewport.x2, ratioX.max, x),
            max: normalize(viewport.x1, ratioX.min, viewport.x2, ratioX.max, x + viewportBBox.width),
        },
        y: {
            min: normalize(viewport.y1, ratioY.min, viewport.y2, ratioY.max, y),
            max: normalize(viewport.y1, ratioY.min, viewport.y2, ratioY.max, y + viewportBBox.height),
        },
    };

    // [min, max] are in the [0, 1] range while preserving the (max - min) difference.
    const diffX = result.x.max - result.x.min;
    const diffY = result.y.max - result.y.min;
    result.x.min = clamp(0, result.x.min, 1 - diffX);
    result.x.max = result.x.min + diffX;
    result.y.min = clamp(0, result.y.min, 1 - diffY);
    result.y.max = result.y.min + diffY;

    return result;
}
